import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { walletAPI } from '../services/api';

const POLICY_TYPES = ['health', 'life', 'auto', 'travel', 'home', 'other'];

export default function AddPolicy() {
  const navigate = useNavigate();
  const [method, setMethod] = useState(0);
  const [insurerFilter, setInsurerFilter] = useState('all');
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Lookup
  const [lookupInsurer, setLookupInsurer] = useState('');
  const [lookupNumber, setLookupNumber] = useState('');
  const [lookupResult, setLookupResult] = useState(null);

  // Manual
  const [manual, setManual] = useState({
    insurer_account_id: '',
    policy_number: '',
    policy_type: 'health',
    start_date: '',
    end_date: '',
    premium_amount: '',
    coverage_details: {}
  });

  // OCR / upload (simplified: paste text or file_url)
  const [ocrText, setOcrText] = useState('');
  const [extracted, setExtracted] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const subcategory = insurerFilter === 'domestic_workers' ? 'domestic_workers' : null;
        const list = await walletAPI.getInsurers(subcategory);
        setInsurers(Array.isArray(list) ? list : (list?.data || []));
      } catch (e) {
        console.warn('Insurers load failed', e);
      }
    })();
  }, [insurerFilter]);

  const handleLookup = async () => {
    if (!lookupInsurer || !lookupNumber) {
      setError('Select insurer and enter policy number');
      return;
    }
    setError(null);
    setLookupResult(null);
    setLoading(true);
    try {
      const res = await walletAPI.lookupPolicy(lookupInsurer, lookupNumber);
      const data = res?.data ?? res;
      setLookupResult(data);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmLookup = async () => {
    const policyData = lookupResult?.policyData ?? lookupResult;
    if (!policyData?.found) return;
    setLoading(true);
    setError(null);
    try {
      const metadata = lookupResult?.metadata || {};
      const payload = {
        insurer_account_id: policyData.insurer_account_id,
        policy_number: policyData.policy_number,
        policy_type: policyData.policy_type || 'health',
        start_date: policyData.start_date || new Date().toISOString().slice(0, 10),
        end_date: policyData.end_date || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        premium_amount: policyData.premium_amount,
        coverage_details: policyData.coverage_details || policyData.raw?.benefits || {},
        source: 'api_sync',
        source_reference: 'insurer_api',
        sourceMetadata: metadata
      };
      await walletAPI.createPolicy(payload);
      setSuccess('Policy added to wallet');
      setTimeout(() => navigate('/wallet'), 1500);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to add policy');
    } finally {
      setLoading(false);
    }
  };

  const handleExtract = async () => {
    if (!ocrText.trim()) {
      setError('Paste OCR text or upload a file');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await walletAPI.uploadDocument({ ocr_text: ocrText });
      const data = res?.data ?? res;
      setExtracted({ ...(data?.extracted || data || {}), _metadata: data?.metadata || {} });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Extraction failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmExtract = async () => {
    if (!extracted) return;
    const metadata = extracted._metadata || {};
    const insurerId = manual.insurer_account_id || insurers[0]?.id;
    if (!insurerId) {
      setError('Select an insurer');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        insurer_account_id: insurerId,
        policy_number: extracted.policyNumber || 'OCR-' + Date.now(),
        policy_type: extracted.policyType || 'health',
        start_date: extracted.startDate || new Date().toISOString().slice(0, 10),
        end_date: extracted.endDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        premium_amount: extracted.premiumAmount,
        coverage_details: extracted.benefits || {},
        source: 'ocr',
        source_reference: 'document_upload',
        sourceMetadata: metadata
      };
      await walletAPI.createPolicy(payload);
      setSuccess('Policy added to wallet');
      setTimeout(() => navigate('/wallet'), 1500);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to add policy');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manual.insurer_account_id || !manual.policy_number || !manual.start_date || !manual.end_date) {
      setError('Insurer, policy number, start date and end date required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...manual,
        premium_amount: manual.premium_amount ? parseFloat(manual.premium_amount) : null
      };
      await walletAPI.createPolicy(payload);
      setSuccess('Policy added to wallet');
      setTimeout(() => navigate('/wallet'), 1500);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to add policy');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>Add Policy</Typography>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <FormControl component="fieldset" sx={{ mb: 2, display: 'block' }}>
        <FormLabel component="legend">Insurance type</FormLabel>
        <RadioGroup row value={insurerFilter} onChange={(e) => setInsurerFilter(e.target.value)}>
          <FormControlLabel value="all" control={<Radio size="small" />} label="All insurance" />
          <FormControlLabel value="domestic_workers" control={<Radio size="small" />} label="Domestic workers" />
        </RadioGroup>
      </FormControl>

      <Tabs value={method} onChange={(_, v) => setMethod(v)} sx={{ mb: 2 }}>
        <Tab label="Lookup" />
        <Tab label="Upload / OCR" />
        <Tab label="Manual" />
      </Tabs>

      {method === 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>Policy number lookup (insurer API)</Typography>
          <TextField select fullWidth label="Insurer" value={lookupInsurer} onChange={(e) => setLookupInsurer(e.target.value)} sx={{ mb: 2 }}>
            {insurers.map((i) => <MenuItem key={i.id} value={i.id}>{i.company_name}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Policy number" value={lookupNumber} onChange={(e) => setLookupNumber(e.target.value)} sx={{ mb: 2 }} />
          <Button variant="contained" onClick={handleLookup} disabled={loading}>Lookup Policy</Button>
          {lookupResult && (
            <Box sx={{ mt: 2 }}>
              {(() => {
                const policyData = lookupResult.policyData ?? lookupResult;
                if (policyData?.found) {
                  return (
                    <>
                      <Alert severity="success" sx={{ mb: 1 }}>Policy found</Alert>
                      <Typography variant="body2">Type: {policyData.policy_type} · End: {policyData.end_date || '—'}</Typography>
                      <Button variant="contained" sx={{ mt: 1 }} onClick={handleConfirmLookup} disabled={loading}>Confirm & Add to Wallet</Button>
                    </>
                  );
                }
                return <Alert severity="info">No policy found or API not configured. Use manual entry or upload.</Alert>;
              })()}
            </Box>
          )}
        </Paper>
      )}

      {method === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>Paste extracted text or upload file (OCR)</Typography>
          <TextField fullWidth multiline rows={4} placeholder="Paste OCR text here…" value={ocrText} onChange={(e) => setOcrText(e.target.value)} sx={{ mb: 2 }} />
          <Button variant="outlined" onClick={handleExtract} disabled={loading}>Extract data</Button>
          {extracted && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">Policy: {extracted.policyNumber || '—'} · Confidence: {extracted.confidence != null ? Math.round(extracted.confidence * 100) + '%' : '—'}</Typography>
              <TextField fullWidth size="small" label="Insurer" select value={manual.insurer_account_id} onChange={(e) => setManual((m) => ({ ...m, insurer_account_id: e.target.value }))} sx={{ mt: 1 }}>
                {insurers.map((i) => <MenuItem key={i.id} value={i.id}>{i.company_name}</MenuItem>)}
              </TextField>
              <Button variant="contained" sx={{ mt: 1 }} onClick={handleConfirmExtract} disabled={loading}>Confirm & Add</Button>
            </Box>
          )}
        </Paper>
      )}

      {method === 2 && (
        <Paper sx={{ p: 2 }}>
          <TextField select fullWidth label="Insurer" value={manual.insurer_account_id} onChange={(e) => setManual((m) => ({ ...m, insurer_account_id: e.target.value }))} sx={{ mb: 2 }}>
            {insurers.map((i) => <MenuItem key={i.id} value={i.id}>{i.company_name}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Policy number" value={manual.policy_number} onChange={(e) => setManual((m) => ({ ...m, policy_number: e.target.value }))} sx={{ mb: 2 }} />
          <TextField select fullWidth label="Policy type" value={manual.policy_type} onChange={(e) => setManual((m) => ({ ...m, policy_type: e.target.value }))} sx={{ mb: 2 }}>
            {POLICY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField fullWidth type="date" label="Start date" value={manual.start_date} onChange={(e) => setManual((m) => ({ ...m, start_date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <TextField fullWidth type="date" label="End date" value={manual.end_date} onChange={(e) => setManual((m) => ({ ...m, end_date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <TextField fullWidth type="number" label="Premium (annual)" value={manual.premium_amount} onChange={(e) => setManual((m) => ({ ...m, premium_amount: e.target.value }))} sx={{ mb: 2 }} />
          <Button variant="contained" fullWidth onClick={handleManualSubmit} disabled={loading}>Add to Wallet</Button>
        </Paper>
      )}

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress size={24} /></Box>}
      <Button sx={{ mt: 2 }} onClick={() => navigate('/wallet')}>Back to Wallet</Button>
    </Container>
  );
}
